import { parseLocale } from "@repo/fika-shared/src/locale"
import { type Locale, setDefaultOptions } from "date-fns"
import { ca, de, enUS, es, fr, it, pt } from "date-fns/locale"
import {
  createEffect,
  createMemo,
  createSignal,
  type ParentProps,
  Show,
} from "solid-js"
import TypesafeI18n from "@/i18n/i18n-solid"
import type { Locales } from "@/i18n/i18n-types"
import { browserLocale, loadLocale, localeFromUrl } from "@/lib/i18n"
import { createSession } from "./session-provider"

const dateLocaleMap: Partial<Record<string, Locale>> = {
  en: enUS,
  es,
  ca,
  fr,
  de,
  pt,
  it,
}

export function I18nProvider(props: ParentProps) {
  const { session } = createSession()

  const [locale, setLocale] = createSignal<Locales>(
    localeFromUrl() ?? browserLocale(),
  )

  createEffect(() => {
    const userFromSession = session().user

    const localeFromUser = userFromSession?.interfaceLocale
    if (localeFromUser) {
      setLocale(localeFromUser)
      return
    }

    if (session().status === "logged-out") {
      const urlLocale = localeFromUrl()
      if (urlLocale) {
        setLocale(urlLocale)
        return
      }

      setLocale(browserLocale())
    }
  })

  const loadedLocale = createMemo(() => {
    const currentLocale = locale()
    if (!currentLocale) return null

    const parsedLocale = parseLocale(currentLocale)
    loadLocale(parsedLocale)
    setDefaultOptions({
      locale: dateLocaleMap[currentLocale],
    })
    return parsedLocale
  })

  return (
    <Show when={loadedLocale()} keyed>
      {locale => <TypesafeI18n locale={locale}>{props.children}</TypesafeI18n>}
    </Show>
  )
}
